/* ============================================================
   ShoLi - Shopping List Manager
   Pure state module (no DOM dependencies)
   ============================================================ */

// === Types ===

export const ItemStatus = {
  OffList: 0,
  Unchecked: 1,
  Checked: 2,
} as const;

export type AddResult =
  | { success: true; kind: "added" }
  | { success: false; kind: "duplicate"; error: "duplicate"; existingId: string };

export interface TodoItem {
  id: string;
  name: string;
  status: 0 | 1 | 2;
}

export interface AppSettings {
  confirmRemoveChecked: boolean;
  confirmRemoveAll: boolean;
  markerChecked: string;
  markerUnchecked: string;
  markerOffList: string;
  mergePolicy: "keep" | "import";
  textSize: string;
  groupByState: boolean;
}

// === Constants ===

export const DEFAULT_SETTINGS: AppSettings = {
  confirmRemoveChecked: false,
  confirmRemoveAll: false,
  markerChecked: "+",
  markerUnchecked: "-",
  markerOffList: "*",
  mergePolicy: "keep",
  textSize: "16",
  groupByState: true,
};

// === State Factory ===

type Listener = () => void;

interface StateStore {
  items: TodoItem[];
  settings: AppSettings;
}

interface StateApi {
  load: () => void;
  save: () => void;
  refresh: () => void;
  subscribe: (fn: Listener) => () => void;
  addItem: (name: string) => AddResult;
  removeItem: (id: string) => boolean;
  toggleOnList: (id: string) => boolean;
  toggleCheck: (id: string) => boolean;
  checkAll: () => void;
  uncheckAll: () => void;
  markOffList: () => number;
  markAllOffList: () => number;
  getSetting: <K extends keyof AppSettings>(key: K) => AppSettings[K];
  loadSettings: () => AppSettings;
  saveSettings: (newSettings: Partial<AppSettings>) => void;
  exportData: () => string;
  importData: (
    text: string,
    mergePolicy?: "keep" | "import",
  ) => { imported: number; updated: number };
  eraseAll: () => void;
  getItems: () => TodoItem[];
  getAllItems: () => TodoItem[];
  getListItems: () => TodoItem[];
  isNameTaken: (name: string) => boolean;
  sortItems: (itemArray: TodoItem[], groupByState: boolean) => TodoItem[];
}

function createStorageKey(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function createState(
  storageKeys: { items: string; settings: string },
  storage: Storage,
): StateApi {
  const store: StateStore = {
    items: [],
    settings: { ...DEFAULT_SETTINGS },
  };
  const listeners: Listener[] = [];
  let _rafId: number | null = null;

  function migrateItems(): void {
    let migrated = false;
    for (const item of store.items) {
      if (!item.id) {
        item.id = createStorageKey();
        migrated = true;
      }
    }
    if (migrated) persist();
  }

  function persist(): void {
    storage.setItem(storageKeys.items, JSON.stringify(store.items));
    storage.setItem(storageKeys.settings, JSON.stringify(store.settings));
  }

  function load(): void {
    try {
      const raw = storage.getItem(storageKeys.items);
      store.items = raw ? JSON.parse(raw) : [];
    } catch {
      store.items = [];
    }
    migrateItems();
    try {
      const raw = storage.getItem(storageKeys.settings);
      store.settings = { ...DEFAULT_SETTINGS, ...(raw && JSON.parse(raw)) };
    } catch {
      store.settings = { ...DEFAULT_SETTINGS };
    }
  }

  function save(): void {
    persist();
  }

  function refresh(): void {
    try {
      const raw = storage.getItem(storageKeys.items);
      store.items = raw ? JSON.parse(raw) : [];
    } catch {
      store.items = [];
    }
  }

  function subscribe(fn: Listener): () => void {
    listeners.push(fn);
    return () => {
      const idx = listeners.indexOf(fn);
      if (idx >= 0) listeners.splice(idx, 1);
    };
  }

  function notify(): void {
    // Use setTimeout in non-browser environments (e.g., tests)
    // Only schedule one callback — subsequent calls coalesce into it
    const schedule =
      typeof requestAnimationFrame !== "undefined"
        ? requestAnimationFrame
        : (((fn: () => void) => setTimeout(fn, 0)) as typeof requestAnimationFrame);
    if (!_rafId) {
      _rafId = schedule(() => {
        _rafId = null;
        for (const fn of listeners) {
          fn();
        }
      });
    }
  }

  // --- Item CRUD ---

  function addItem(name: string): AddResult {
    const trimmed = name.trim();
    const idx = store.items.findIndex((i) => i.name.toLowerCase() === trimmed.toLowerCase());
    if (idx >= 0) {
      return {
        success: false,
        kind: "duplicate",
        error: "duplicate",
        existingId: store.items[idx].id,
      };
    }
    const item: TodoItem = {
      id: createStorageKey(),
      name: trimmed,
      status: ItemStatus.Unchecked,
    };
    store.items.push(item);
    persist();
    notify();
    return { success: true, kind: "added" };
  }

  function removeItem(id: string): boolean {
    const idx = store.items.findIndex((i) => i.id === id);
    if (idx === -1) return false;
    store.items.splice(idx, 1);
    persist();
    notify();
    return true;
  }

  function toggleOnList(id: string): boolean {
    const idx = store.items.findIndex((i) => i.id === id);
    if (idx === -1) return false;
    const item = store.items[idx];
    item.status = item.status === ItemStatus.Unchecked ? ItemStatus.OffList : ItemStatus.Unchecked;
    persist();
    notify();
    return true;
  }

  function toggleCheck(id: string): boolean {
    const idx = store.items.findIndex((i) => i.id === id);
    if (idx === -1) return false;
    const item = store.items[idx];
    item.status = item.status === ItemStatus.Unchecked ? ItemStatus.Checked : ItemStatus.Unchecked;
    persist();
    notify();
    return true;
  }

  // --- Bulk Operations ---

  function checkAll(): void {
    for (const item of store.items) {
      if (item.status === ItemStatus.Unchecked) item.status = ItemStatus.Checked;
    }
    persist();
    notify();
  }

  function uncheckAll(): void {
    for (const item of store.items) {
      if (item.status === ItemStatus.Checked) item.status = ItemStatus.Unchecked;
    }
    persist();
    notify();
  }

  function markOffList(): number {
    let count = 0;
    for (const item of store.items) {
      if (item.status === ItemStatus.Checked) {
        item.status = ItemStatus.OffList;
        count++;
      }
    }
    persist();
    notify();
    return count;
  }

  function markAllOffList(): number {
    const count = store.items.length;
    for (const i of store.items) {
      i.status = ItemStatus.OffList;
    }
    persist();
    notify();
    return count;
  }

  // --- Settings ---

  function getSetting<K extends keyof AppSettings>(key: K): AppSettings[K] {
    return store.settings[key];
  }

  function loadSettings(): AppSettings {
    return { ...store.settings };
  }

  function saveSettings(newSettings: Partial<AppSettings>): void {
    store.settings = { ...DEFAULT_SETTINGS, ...newSettings };
    persist();
    notify();
  }

  // --- Data ---

  function exportData(): string {
    const lines = store.items.map((item) => {
      const marker =
        item.status === ItemStatus.OffList
          ? store.settings.markerOffList
          : item.status === ItemStatus.Unchecked
            ? store.settings.markerUnchecked
            : store.settings.markerChecked;
      return `${marker} ${item.name}`;
    });
    return lines.join("\n");
  }

  function importData(
    text: string,
    mergePolicy?: "keep" | "import",
  ): { imported: number; updated: number } {
    const policy = mergePolicy || store.settings.mergePolicy;
    const lines = text
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    let imported = 0;
    let updated = 0;

    lines.forEach((line) => {
      const marker = line.charAt(0);
      const name = line.slice(1).trim();
      if (!name) return;

      let targetStatus: 0 | 1 | 2;
      if (marker === store.settings.markerChecked) targetStatus = ItemStatus.Checked;
      else if (marker === store.settings.markerUnchecked) targetStatus = ItemStatus.Unchecked;
      else targetStatus = ItemStatus.OffList;

      const existingIdx = store.items.findIndex((i) => i.name.toLowerCase() === name.toLowerCase());
      if (existingIdx >= 0) {
        if (policy === "import") {
          store.items[existingIdx].status = targetStatus;
        }
        updated++;
      } else {
        store.items.push({ id: createStorageKey(), name, status: targetStatus });
        imported++;
      }
    });

    persist();
    notify();
    return { imported, updated };
  }

  function eraseAll(): void {
    store.items = [];
    persist();
    notify();
  }

  // --- Getters/Setters ---

  function getItems(): TodoItem[] {
    return store.items;
  }
  function getAllItems(): TodoItem[] {
    return [...store.items];
  }
  function getListItems(): TodoItem[] {
    return store.items.filter((i) => i.status !== ItemStatus.OffList);
  }
  function isNameTaken(name: string): boolean {
    return store.items.some((i) => i.name.toLowerCase() === name.trim().toLowerCase());
  }

  // --- Sorting helper ---
  function sortItems(itemArray: TodoItem[], groupByState: boolean): TodoItem[] {
    const sorted = [...itemArray];
    if (groupByState) {
      sorted.sort((a, b) => {
        if (a.status !== b.status) return Number(a.status) - Number(b.status);
        return a.name.localeCompare(b.name, "en");
      });
    } else {
      sorted.sort((a, b) => a.name.localeCompare(b.name, "en"));
    }
    return sorted;
  }

  return {
    load,
    save,
    refresh,
    subscribe,
    addItem,
    removeItem,
    toggleOnList,
    toggleCheck,
    checkAll,
    uncheckAll,
    markOffList,
    markAllOffList,
    getSetting,
    loadSettings,
    saveSettings,
    exportData,
    importData,
    eraseAll,
    getItems,
    getAllItems,
    getListItems,
    isNameTaken,
    sortItems,
  };
}
